import { i18n } from "@/locales/settings";
import { LocalesFields } from "@/locales/translateInterface";
import {
  currentUserIsCompanyOwner,
  getCurrentEmployee,
  isRoot
} from "@/plugins/roles";
import {
  bookkeepingModule,
  integrationModule,
  learningControlModule,
  learningDocumentsModule,
  webinarRoomModule,
  informationModule,
  learningGroupModule,
  agentsModule
} from "@/plugins/modules-detect";
import userStore from "@/store/modules/users/currentUserInfo";

/**
 * Interface of single menu item object.
 */
export interface IMenuItem {
  title: string;
  route?: {
    name: string;
    params?: {
      bundle?: boolean;
      roles?: boolean;
    };
  };
  childs?: IMenuItem[];
}

export class MenuItem {
  companyOwner: boolean = currentUserIsCompanyOwner();
  root = isRoot();
  learningDocuments: boolean;
  bookkeeping: boolean;
  integrations: boolean;
  learningControl: boolean;
  webinarRoom: boolean;
  information: boolean;
  learningGroupsModule: boolean;
  roles: Array<string> = getCurrentEmployee() ? getCurrentEmployee().roles : [];

  contentManager: boolean = this.roles.includes("content_manager");
  learningRequestManager: boolean = this.roles.includes(
    "learning_request_manager"
  );
  documentManager: boolean = this.roles.includes("document_manager");

  constructor() {
    this.learningDocuments = learningDocumentsModule();
    this.bookkeeping = bookkeepingModule();
    this.integrations = integrationModule();
    this.learningControl = learningControlModule();
    this.webinarRoom = webinarRoomModule(); //скорее всего не пригодится
    this.information = informationModule();
    this.learningGroupsModule = learningGroupModule();
  }

  get isContractor() {
    return userStore.state.user.isContractor ?? false;
  }

  courses: IMenuItem = {
    title: i18n.tc(LocalesFields.Courses),
    route: {
      name: "courses"
    }
  };

  lecture_list: IMenuItem = {
    title: i18n.tc(LocalesFields.Lectures),
    route: {
      name: "lectureList",
      params: {
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  webinars: IMenuItem = {
    title: i18n.tc(LocalesFields.Webinars),
    route: {
      name: "webinars"
    }
  };

  records: IMenuItem = {
    title: i18n.tc(LocalesFields.Records),
    route: {
      name: "records"
    }
  };

  // Группа продаж
  learning_request: IMenuItem = {
    title: i18n.tc(LocalesFields.LearningRequests),
    route: {
      name: "orders",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.learningRequestManager ||
          this.isContractor
      }
    }
  };

  educational: IMenuItem = {
    title: i18n.tc(LocalesFields.InformationAboutEducationalOrganization),
    route: {
      name: "EducationalOrganization",
      params: {
        bundle: informationModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.learningRequestManager ||
          this.contentManager ||
          this.documentManager
      }
    }
  };

  contract: IMenuItem = {
    title: i18n.tc(LocalesFields.Contracts),
    route: {
      name: "contracts",
      params: {
        bundle: bookkeepingModule(),
        roles: this.root || this.companyOwner || this.documentManager
      }
    }
  };

  createContract: IMenuItem = {
    title: i18n.tc(LocalesFields.Contract),
    route: {
      name: "createContract",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root || this.companyOwner || this.documentManager
      }
    }
  };

  bill: IMenuItem = {
    title: i18n.tc(LocalesFields.Bills),
    route: {
      name: "bills",
      params: {
        bundle: bookkeepingModule(),
        roles: this.root || this.companyOwner || this.documentManager
      }
    }
  };

  students: IMenuItem = {
    title: i18n.tc(LocalesFields.Students),
    route: {
      name: "students",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  act: IMenuItem = {
    title: i18n.tc(LocalesFields.CertificatesOfCompletedWorks),
    route: {
      name: "acts",
      params: {
        bundle: bookkeepingModule(),
        roles: this.root || this.companyOwner || this.documentManager
      }
    }
  };

  // Группа панели управления
  company: IMenuItem = {
    title: i18n.tc(LocalesFields.Companies),
    route: {
      name: "companies",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  companyTests: IMenuItem = {
    title: i18n.tc(LocalesFields.Tests),
    route: {
      name: "testList",
      params: {
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  companyResultTests: IMenuItem = {
    title: i18n.tc(LocalesFields.TestResults),
    route: {
      name: "testResultCompany",
      params: {
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  contractor: IMenuItem = {
    title: i18n.tc(LocalesFields.Contractors),
    route: {
      name: "contractorList",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager ||
          this.isContractor
      }
    }
  };

  practicePassList: IMenuItem = {
    title: i18n.tc(LocalesFields.PracticeAssignmentResults),
    route: {
      name: "practiceList",
      params: {
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  taxonomyActivity: IMenuItem = {
    title: i18n.tc(
      LocalesFields.FISFRDOFederalInformationSystemTheFederalRegisterOfDataOnEducationDocumentsAndorAboutQualificationDocumentsOnTraining
    ),
    route: {
      name: "ProfessionalActivityTaxonomyList",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root
      }
    }
  };

  category: IMenuItem = {
    title: i18n.tc(LocalesFields.DirectionsOfLearning),
    route: {
      name: "categoryList",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  users: IMenuItem = {
    title: i18n.tc(LocalesFields.Users),
    route: {
      name: "users",
      params: {
        roles: this.root
      }
    }
  };

  importCourses: IMenuItem = {
    title: i18n.tc(LocalesFields.ImportCourses),
    route: {
      name: "importCourses",
      params: {
        roles: this.root
      }
    }
  };

  instructions: IMenuItem = {
    title: i18n.tc(LocalesFields.Instructions),
    route: {
      name: "instructions",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  apiGenerating: IMenuItem = {
    title: i18n.tc(LocalesFields.Integration),
    route: {
      name: "apiKeysMain",
      params: {
        bundle: integrationModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  documentType: IMenuItem = {
    title: i18n.tc(LocalesFields.DocumentType),
    route: {
      name: "documentTypeTaxonomyList",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root
      }
    }
  };

  myPractice: IMenuItem = {
    title: i18n.tc(LocalesFields.MyPracticalAssignments),
    route: {
      name: "myPractices"
    }
  };

  myTestResult: IMenuItem = {
    title: i18n.tc(LocalesFields.MyTestResults),
    route: {
      name: "testResult"
    }
  };

  educationProtocolList: IMenuItem = {
    title: i18n.tc(LocalesFields.TrainingProtocols),
    route: {
      name: "educationProtocolList",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  reportList: IMenuItem = {
    title: i18n.tc(LocalesFields.AgentReports),
    route: {
      name: "reports",
      params: {
        bundle: agentsModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  agentList: IMenuItem = {
    title: i18n.tc(LocalesFields.Agents),
    route: {
      name: "agents",
      params: {
        bundle: agentsModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  conditionList: IMenuItem = {
    title: i18n.tc(LocalesFields.PaymentTerms),
    route: {
      name: "conditionList",
      params: {
        bundle: agentsModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  importList: IMenuItem = {
    title: i18n.tc(LocalesFields.Import),
    route: {
      name: "ImportList",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.documentManager ||
          this.learningRequestManager
      }
    }
  };

  createEducationProtocol: IMenuItem = {
    title: i18n.tc(LocalesFields.TrainingProtocol),
    route: {
      name: "educationProtocolNew",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  // Группа добавления материала
  addUser: IMenuItem = {
    title: i18n.tc(LocalesFields.User),
    route: {
      name: "registration",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  addCourse: IMenuItem = {
    title: i18n.tc(LocalesFields.Course),
    childs: [
      {
        title: i18n.tc(LocalesFields.Course),
        route: {
          name: "courseNew",
          params: {
            roles: this.root || this.companyOwner || this.contentManager
          }
        }
      },
      {
        title: "SCORM",
        route: {
          name: "courseScormCreate",
          params: {
            roles: this.root || this.companyOwner || this.contentManager
          }
        }
      }
    ]
  };

  addLecture: IMenuItem = {
    title: i18n.tc(LocalesFields.Lecture),
    route: {
      name: "lectureNew",
      params: {
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  addTest: IMenuItem = {
    title: i18n.tc(LocalesFields.Test),
    route: {
      name: "testNew",
      params: {
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  addPractice: IMenuItem = {
    title: i18n.tc(LocalesFields.PracticeAssignment),
    route: {
      name: "practiceNew",
      params: {
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  addWebinar: IMenuItem = {
    title: i18n.tc(LocalesFields.Webinar),
    route: {
      name: "webinarNew",
      params: {
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  addRecord: IMenuItem = {
    title: i18n.tc(LocalesFields.Record),
    route: {
      name: "recordNew",
      params: {
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  addContractor: IMenuItem = {
    title: i18n.tc(LocalesFields.Contractor),
    route: {
      name: "createContractor",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.learningRequestManager ||
          this.documentManager
      }
    }
  };

  addLearningRequest: IMenuItem = {
    title: i18n.tc(LocalesFields.LearningRequest),
    route: {
      name: "createOrder",
      params: {
        roles: this.root || this.companyOwner || this.learningRequestManager
      }
    }
    // childs: [
    //   {
    //     title: i18n.tc(LocalesFields.Course),
    //     route: {
    //       name: "createOrder",
    //       params: {
    //         roles: this.root || this.companyOwner || this.learningRequestManager
    //       }
    //     }
    //   },
    //   {
    //     title: "SCORM",
    //     route: {
    //       name: "learningRequestCreate",
    //       params: {
    //         roles: this.root || this.companyOwner || this.learningRequestManager
    //       }
    //     }
    //   }
    // ]
  };

  finalDocumentList: IMenuItem = {
    title: i18n.tc(LocalesFields.FinalDocuments),
    route: {
      name: "finalDocumentList",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  learningProgress: IMenuItem = {
    title: i18n.tc(LocalesFields.LearnerProgress),
    route: {
      name: "learningProgress",
      params: {
        bundle: learningControlModule(),
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  importExport: IMenuItem = {
    title: `${i18n.tc(LocalesFields.Import)}/${i18n.tc(LocalesFields.Export)}`,
    route: {
      name: "importExport",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.documentManager ||
          this.learningRequestManager
      }
    }
  };

  exportList: IMenuItem = {
    title: i18n.tc(LocalesFields.Export),
    route: {
      name: "exportList",
      params: {
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.documentManager ||
          this.learningRequestManager
      }
    }
  };

  documentsNumberList: IMenuItem = {
    title: i18n.tc(LocalesFields.DocumentNumbering),
    route: {
      name: "documentNumbers",
      params: {
        bundle: learningDocumentsModule() || bookkeepingModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.documentManager ||
          this.learningRequestManager
      }
    }
  };

  learningRequestProfiles: IMenuItem = {
    title: i18n.tc(LocalesFields.ApplicationProfiles),
    route: {
      name: "profiles",
      params: {
        bundle: learningDocumentsModule() || bookkeepingModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.documentManager ||
          this.learningRequestManager
      }
    }
  };

  enrollmentOrder: IMenuItem = {
    title: i18n.tc(LocalesFields.OrdersOfEnrollment),
    route: {
      name: "enrollmentList",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  createEnrollmentOrder: IMenuItem = {
    title: i18n.tc(LocalesFields.OrderOfEnrollment),
    route: {
      name: "enrollmentCreate",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  expulsionOrder: IMenuItem = {
    title: i18n.tc(LocalesFields.OrdersOfCompletion),
    route: {
      name: "expulsionList",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  createExpulsionOrder: IMenuItem = {
    title: i18n.tc(LocalesFields.OrderOfCompletion),
    route: {
      name: "ExpulsionOrderNew",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  createFinalDocument: IMenuItem = {
    title: i18n.tc(LocalesFields.FinalDocument),
    route: {
      name: "finalDocumentNew",
      params: {
        bundle: learningDocumentsModule(),
        roles: this.root || this.companyOwner || this.contentManager
      }
    }
  };

  learningGroups: IMenuItem = {
    title: i18n.tc(LocalesFields.TrainingGroups),
    route: {
      name: "learningGroupList",
      params: {
        bundle: learningGroupModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.documentManager ||
          this.learningRequestManager
      }
    }
  };

  documentTemplates: IMenuItem = {
    title: i18n.tc(LocalesFields.DocumentTemplates),
    route: {
      name: "docTemplates",
      params: {
        bundle: learningDocumentsModule() || bookkeepingModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.contentManager ||
          this.documentManager ||
          this.learningRequestManager
      }
    }
  };

  createDocumentTemplates: IMenuItem = {
    title: i18n.tc(LocalesFields.Template),
    route: {
      name: "docTemplatesCreate",
      params: {
        bundle: learningDocumentsModule() || bookkeepingModule(),
        roles:
          this.root ||
          this.companyOwner ||
          this.learningRequestManager ||
          this.contentManager ||
          this.documentManager
      }
    }
  };

  get _sales() {
    return this.checkAccess([
      this.learning_request,
      this.contract,
      this.bill,
      this.act
    ]);
  }

  checkAccess(list: Array<IMenuItem>) {
    let routes = list
      .filter(el =>
        el.route?.params?.bundle !== undefined ? el.route?.params?.bundle : el
      )
      .filter(el =>
        el.route?.params?.roles !== undefined ? el.route?.params?.roles : el
      );
    return routes.length > 0 ? routes : undefined;
  }

  get _panel() {
    let allPanelRoutes: Array<IMenuItem> = [
      this.category,
      this.taxonomyActivity,
      this.company,
      this.contractor,
      this.users,
      this.students,
      this.apiGenerating,
      this.documentTemplates,
      this.documentType,
      this.importCourses,
      this.documentsNumberList,
      // this.learningRequestProfiles,
      this.educational,
      this.importList,
      this.exportList,
      this.agentList,
      this.conditionList
      // this.instructions
    ];
    return this.checkAccess(allPanelRoutes);
  }

  get _fixedAddMenu() {
    return this.checkAccess([
      this.addLearningRequest,
      this.createContract,
      this.createEnrollmentOrder,
      this.createExpulsionOrder,
      this.createEducationProtocol,
      this.createFinalDocument,
      this.addCourse,
      this.addLecture,
      this.addTest,
      this.addPractice,
      this.addWebinar,
      this.addRecord,
      this.addContractor,
      this.addUser,
      this.createDocumentTemplates,
      this.educational
    ]);
  }

  /**
   * Default menu items for all users
   */
  get _learningMaterials() {
    return this.checkAccess([
      this.courses,
      this.webinars,
      this.records,
      this.lecture_list,
      this.companyTests
    ]);
  }

  get _studyDocuments() {
    return this.checkAccess([
      this.educationProtocolList,
      this.finalDocumentList,
      this.enrollmentOrder,
      this.expulsionOrder
    ]);
  }

  get _educationControl() {
    return this.checkAccess([
      this.practicePassList,
      this.companyResultTests,
      this.learningProgress,
      this.learningGroups
    ]);
  }

  get _reports() {
    return this.checkAccess([
      this.myPractice,
      this.myTestResult,
      this.reportList
    ]);
  }
}

export const customMenu = () => {
  let UserMenu = new MenuItem();

  return {
    learning: UserMenu._learningMaterials,
    sales: UserMenu._sales,
    controlPanel: UserMenu._panel,
    fixedAddMenu: UserMenu._fixedAddMenu,
    reports: UserMenu._reports,
    educationControl: UserMenu._educationControl,
    studyDocuments: UserMenu._studyDocuments
  };
};
